/**
 * Onboarding Wizard - Premium User Onboarding Experience
 * Version: 6.2.0
 * 
 * 6-Step Flow:
 * 1. Create Profile - Set up your trading profile
 * 2. Connect Broker - Link your trading account
 * 3. Create Strategy - Choose or build a strategy
 * 4. Backtest - Test your strategy on historical data
 * 5. Paper Trade - Practice without real money
 * 6. Go Live - Start real trading
 */
import { useState } from 'react'
import './OnboardingWizard.css'

interface OnboardingStep {
  id: string
  title: string
  description: string
  icon: string
  learnMore?: string
}

const ONBOARDING_STEPS: OnboardingStep[] = [
  { id: 'profile', title: 'Create Profile', description: 'Set up your trading profile and preferences', icon: '👤', learnMore: 'Your profile helps us personalize your trading experience' },
  { id: 'broker', title: 'Connect Broker', description: 'Link your trading account', icon: '🔗', learnMore: 'Connect to 82+ brokers including Zerodha, Angel One, and crypto exchanges' },
  { id: 'strategy', title: 'Create Strategy', description: 'Choose or build your trading strategy', icon: '📊', learnMore: 'Use AI to create strategies or choose from pre-built templates' },
  { id: 'backtest', title: 'Backtest', description: 'Test strategy on historical data', icon: '🔬', learnMore: 'Validate your strategy before risking real money' },
  { id: 'paper', title: 'Paper Trade', description: 'Practice with simulated trading', icon: '📝', learnMore: 'Practice trading without any financial risk' },
  { id: 'live', title: 'Go Live', description: 'Start real trading', icon: '🚀', learnMore: 'Execute trades with real money when ready' },
]

interface OnboardingWizardProps {
  onComplete: () => void
  onSkip: () => void
}

function OnboardingWizard({ onComplete, onSkip }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [completedSteps, setCompletedSteps] = useState<string[]>([])

  const totalSteps = ONBOARDING_STEPS.length
  const progress = ((currentStep + 1) / totalSteps) * 100

  const goNext = () => {
    const stepId = ONBOARDING_STEPS[currentStep].id
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId])
    }
    
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      markOnboardingComplete()
      onComplete()
    }
  }

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const goToStep = (index: number) => {
    if (index <= currentStep || completedSteps.includes(ONBOARDING_STEPS[index].id)) {
      setCurrentStep(index)
    }
  }

  const markOnboardingComplete = () => {
    localStorage.setItem('onboarding_completed', 'true')
    localStorage.setItem('onboarding_date', new Date().toISOString())
  }

  const renderStepContent = () => {
    switch (ONBOARDING_STEPS[currentStep].id) {
      case 'profile':
        return <ProfileStep onNext={goNext} />
      case 'broker':
        return <BrokerSetupStep onNext={goNext} />
      case 'strategy':
        return <StrategySelectionStep onNext={goNext} />
      case 'backtest':
        return <BacktestStep onNext={goNext} />
      case 'paper':
        return <PaperTradeStep onNext={goNext} />
      case 'live':
        return <LiveTradingStep />
      default:
        return null
    }
  }

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-wizard">
        {/* Header */}
        <div className="wizard-header">
          <div className="wizard-logo">
            <span className="logo-icon">🚀</span>
            <span className="logo-text">ZO BULL</span>
          </div>
          <button className="wizard-close" onClick={onSkip}>
            Skip Setup
          </button>
        </div>

        {/* Progress */}
        <div className="wizard-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="progress-steps">
            {ONBOARDING_STEPS.map((step, index) => (
              <button
                key={step.id}
                className={`progress-step ${index === currentStep ? 'active' : ''} ${completedSteps.includes(step.id) ? 'completed' : ''}`}
                onClick={() => goToStep(index)}
              >
                <span className="step-icon">{step.icon}</span>
                <span className="step-label">{step.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="wizard-content">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="wizard-footer">
          <div className="footer-info">
            Step {currentStep + 1} of {totalSteps}
          </div>
          <div className="footer-actions">
            {currentStep > 0 && (
              <button className="btn btn-ghost" onClick={goBack}>
                ← Back
              </button>
            )}
            <button className="btn btn-primary" onClick={goNext}>
              {currentStep === totalSteps - 1 ? 'Get Started' : 'Continue →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Profile Step
interface ProfileStepProps {
  onNext: () => void
}

function ProfileStep({ onNext }: ProfileStepProps) {
  const [profileName, setProfileName] = useState('')
  const [experience, setExperience] = useState('beginner')
  
  return (
    <div className="step-content profile-step">
      <div className="step-icon-large">👤</div>
      <h1>Create Your Profile</h1>
      <p className="step-description">Tell us about yourself to personalize your experience</p>
      
      <div className="profile-form">
        <div className="form-group">
          <label>Your Name</label>
          <input 
            type="text" 
            placeholder="Enter your name"
            value={profileName}
            onChange={(e) => setProfileName(e.target.value)}
            className="form-input"
          />
        </div>
        
        <div className="form-group">
          <label>Trading Experience</label>
          <div className="experience-options">
            {['beginner', 'intermediate', 'advanced'].map((level) => (
              <button
                key={level}
                className={`experience-btn ${experience === level ? 'selected' : ''}`}
                onClick={() => setExperience(level)}
              >
                <span className="exp-icon">
                  {level === 'beginner' && '🌱'}
                  {level === 'intermediate' && '📈'}
                  {level === 'advanced' && '🎯'}
                </span>
                <span className="exp-label">{level.charAt(0).toUpperCase() + level.slice(1)}</span>
              </button>
            ))}
          </div>
        </div>
        
        <div className="form-group">
          <label>Trading Goals</label>
          <div className="goals-options">
            {[
              { id: 'passive', label: 'Passive Income', icon: '💰' },
              { id: 'active', label: 'Active Trading', icon: '📊' },
              { id: 'hedge', label: 'Portfolio Hedge', icon: '🛡️' },
              { id: 'learn', label: 'Learning', icon: '📚' },
            ].map((goal) => (
              <label key={goal.id} className="goal-checkbox">
                <input type="checkbox" />
                <span className="goal-icon">{goal.icon}</span>
                <span className="goal-label">{goal.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// Broker Setup Step
interface BrokerSetupStepProps {
  onNext: () => void
}

function BrokerSetupStep({ onNext }: BrokerSetupStepProps) {
  const [selectedBroker, setSelectedBroker] = useState<string | null>(null)
  
  const popularBrokers = [
    { id: 'zerodha', name: 'Zerodha', icon: '📊', users: '2.5M+', markets: 'NSE, BSE, MCX' },
    { id: 'angelone', name: 'Angel One', icon: '🧿', users: '5M+', markets: 'NSE, BSE, MCX, NCDEX' },
    { id: 'upstox', name: 'Upstox', icon: '📈', users: '3M+', markets: 'NSE, BSE, MCX' },
    { id: 'fyers', name: 'Fyers', icon: '🎯', users: '1.5M+', markets: 'NSE, BSE' },
  ]

  const cryptoExchanges = [
    { id: 'binance', name: 'Binance', icon: '🟡', markets: 'Spot, Futures' },
    { id: 'bybit', name: 'Bybit', icon: '🟠', markets: 'Spot, Derivatives' },
    { id: 'okx', name: 'OKX', icon: '⚫', markets: 'Spot, Futures' },
  ]
  
  return (
    <div className="step-content broker-step">
      <h2>Connect Your Broker</h2>
      <p className="step-description">Link your trading account to start automated trading</p>
      
      <div className="broker-section">
        <h3>Indian Brokers</h3>
        <div className="broker-grid">
          {popularBrokers.map((broker) => (
            <button
              key={broker.id}
              className={`broker-card ${selectedBroker === broker.id ? 'selected' : ''}`}
              onClick={() => setSelectedBroker(broker.id)}
            >
              <span className="broker-icon">{broker.icon}</span>
              <span className="broker-name">{broker.name}</span>
              <span className="broker-markets">{broker.markets}</span>
            </button>
          ))}
        </div>
      </div>
      
      <div className="broker-section">
        <h3>Crypto Exchanges</h3>
        <div className="broker-grid">
          {cryptoExchanges.map((exchange) => (
            <button
              key={exchange.id}
              className={`broker-card ${selectedBroker === exchange.id ? 'selected' : ''}`}
              onClick={() => setSelectedBroker(exchange.id)}
            >
              <span className="broker-icon">{exchange.icon}</span>
              <span className="broker-name">{exchange.name}</span>
              <span className="broker-markets">{exchange.markets}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="skip-option">
        <button className="skip-link" onClick={onNext}>
          I'll connect my broker later →
        </button>
      </div>
    </div>
  )
}

// Strategy Selection Step
interface StrategySelectionStepProps {
  onNext: () => void
}

function StrategySelectionStep({ onNext }: StrategySelectionStepProps) {
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null)
  
  const strategies = [
    { id: 'rsi', name: 'RSI Reversal', icon: '📉', winRate: '68%', description: 'Mean reversion on RSI', risk: 'Medium' },
    { id: 'macd', name: 'MACD Crossover', icon: '📊', winRate: '72%', description: 'Trend following', risk: 'Low' },
    { id: 'ema', name: 'EMA Crossover', icon: '📈', winRate: '75%', description: 'Multi-EMA trend', risk: 'Low' },
    { id: 'ai', name: 'AI Strategy', icon: '🧠', winRate: 'Variable', description: 'AI-generated strategy', risk: 'Variable' },
  ]

  return (
    <div className="step-content strategy-step">
      <h2>Choose Your Strategy</h2>
      <p className="step-description">Select a pre-built strategy or create your own with AI</p>
      
      <div className="strategy-list">
        {strategies.map((strategy) => (
          <button
            key={strategy.id}
            className={`strategy-option ${selectedStrategy === strategy.id ? 'selected' : ''}`}
            onClick={() => setSelectedStrategy(strategy.id)}
          >
            <span className="strategy-icon">{strategy.icon}</span>
            <div className="strategy-info">
              <span className="strategy-name">{strategy.name}</span>
              <span className="strategy-desc">{strategy.description}</span>
            </div>
            <div className="strategy-meta">
              <span className="strategy-winrate">{strategy.winRate} win rate</span>
              <span className={`strategy-risk risk-${strategy.risk.toLowerCase()}`}>{strategy.risk}</span>
            </div>
          </button>
        ))}
      </div>

      <div className="skip-option">
        <button className="skip-link" onClick={onNext}>
          I'll choose later →
        </button>
      </div>
    </div>
  )
}

// Backtest Step
interface BacktestStepProps {
  onNext: () => void
}

function BacktestStep({ onNext }: BacktestStepProps) {
  return (
    <div className="step-content backtest-step">
      <div className="step-icon-large">🔬</div>
      <h1>Backtest Your Strategy</h1>
      <p className="step-description">Test your strategy on historical data before risking real money</p>
      
      <div className="backtest-info">
        <div className="info-card">
          <span className="info-icon">📊</span>
          <div className="info-content">
            <h4>Historical Data</h4>
            <p>Test on 5+ years of historical market data</p>
          </div>
        </div>
        <div className="info-card">
          <span className="info-icon">⚡</span>
          <div className="info-content">
            <h4>Fast Results</h4>
            <p>Get backtest results in seconds</p>
          </div>
        </div>
        <div className="info-card">
          <span className="info-icon">📈</span>
          <div className="info-content">
            <h4>Performance Metrics</h4>
            <p>View detailed analytics and reports</p>
          </div>
        </div>
      </div>

      <div className="skip-option">
        <button className="skip-link" onClick={onNext}>
          Skip backtesting →
        </button>
      </div>
    </div>
  )
}

// Paper Trade Step
interface PaperTradeStepProps {
  onNext: () => void
}

function PaperTradeStep({ onNext }: PaperTradeStepProps) {
  return (
    <div className="step-content paper-step">
      <div className="step-icon-large">📝</div>
      <h1>Practice with Paper Trading</h1>
      <p className="step-description">Simulate real trading without any financial risk</p>
      
      <div className="paper-info">
        <div className="info-card">
          <span className="info-icon">💵</span>
          <div className="info-content">
            <h4>₹10,00,000 Virtual Cash</h4>
            <p>Start with ₹10 lakh in virtual money</p>
          </div>
        </div>
        <div className="info-card">
          <span className="info-icon">📱</span>
          <div className="info-content">
            <h4>Real Market Prices</h4>
            <p>Trade with live market data</p>
          </div>
        </div>
        <div className="info-card">
          <span className="info-icon">🏆</span>
          <div className="info-content">
            <h4>Track Performance</h4>
            <p>Monitor your virtual P&L</p>
          </div>
        </div>
      </div>

      <div className="skip-option">
        <button className="skip-link" onClick={onNext}>
          Skip paper trading →
        </button>
      </div>
    </div>
  )
}

// Live Trading Step
function LiveTradingStep() {
  return (
    <div className="step-content live-step">
      <div className="step-icon-large">🚀</div>
      <h1>Ready to Go Live!</h1>
      <p className="step-description">Start real trading when you're comfortable</p>
      
      <div className="live-warning">
        <span className="warning-icon">⚠️</span>
        <p>Live trading involves real financial risk. Make sure you've:</p>
        <ul>
          <li>✅ Completed backtesting</li>
          <li>✅ Practiced with paper trading</li>
          <li>✅ Understand the risks involved</li>
        </ul>
      </div>

      <div className="next-steps">
        <h4>What's Next?</h4>
        <div className="next-step-item">
          <span className="step-number">1</span>
          <span>Fund your trading account</span>
        </div>
        <div className="next-step-item">
          <span className="step-number">2</span>
          <span>Start with small positions</span>
        </div>
        <div className="next-step-item">
          <span className="step-number">3</span>
          <span>Monitor and adjust</span>
        </div>
      </div>
    </div>
  )
}

export default OnboardingWizard